import { Select, SelectContent, SelectItem } from "@/components/ui/select"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"

import * as SelectPrimitive from "@radix-ui/react-select"
import { TriangleAlert } from "lucide-react"
import { useEditStatementVerdict } from "../../../../../api/declarationsApi"
import { formatDate } from "../../../../../utils/date"
import { unknownEditionResolved } from "../../studio/cellState"
import { editionDisplayName } from "../mapping/editionDisplayName"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import type { StatementCard } from "../useStudioV2State"

export interface EditionRowProps {
  declId: string
  card: StatementCard
  /** The active Verordnung's edition axis (`state.editionInfo`), or null —
   *  a Verordnung with no edition catalogue, no selection and no citation
   *  renders NO row at all (the row must never be noise on frameworks
   *  without a reference-list axis: RoHS exemptions etc.). */
  editionInfo: FrameworkEditionInfo | null
}

/**
 * REFERENZLISTE, per statement — v1's per-statement reference-list select,
 * restored (V2 initially dropped it, leaving the "document cites an edition
 * our catalogue does not hold" case a dead end: the statement stayed open
 * with nothing to act on). A quiet meta-row in the ARTIKEL row's visual
 * family, deliberately NOT hidden behind an options toggle: the edition is
 * part of the claim itself ("Konform" always means "konform gegen Stand X"),
 * and the one case that needs action must escalate visibly, not one level
 * deeper.
 *
 * Two states:
 * - Quiet: the effective version (the card's own `listVersion`, named via
 *   the catalogue) or, when the statement carries none, the edition the run
 *   reads the framework against ("Gelesen gegen: …" — v1's silence case).
 * - Warning (amber): the document cited an edition the catalogue does not
 *   hold, quoted verbatim (v1's third case, told apart from silence). The
 *   picker is the resolution.
 *
 * Writing goes through `useEditStatementVerdict` with `verdict: null` — the
 * documented LIST-VERSION-ONLY edit (types.ts: "the reference list is the
 * one that moves on its own, so it must be sendable without re-asserting a
 * verdict"). Errors toast via that hook's own onError; no second handler.
 *
 * The dropdown follows `GeltungSelect`'s post-native pattern: bare Radix
 * trigger carrying the sv2 pill look, app-standard `SelectContent` (global
 * popover tokens, portal-safe).
 */
export function EditionRow({ declId, card, editionInfo }: EditionRowProps) {
  const { translate } = useTranslation()
  const editVerdict = useEditStatementVerdict()
  if (!editionInfo) return null

  // The unresolved-citation warning must close the moment either half of
  // `unknownEditionResolved` (studio/cellState.ts, twinned with the backend's
  // `unknown_edition_resolved` gate) settles the gap: the framework axis was
  // CONFIRMED (`chosen`), or this statement carries its own `listVersion`. A
  // card that no longer needs the warning falls through to the quiet state
  // below — no separate "resolved" branch, since the quiet state already
  // names the effective version.
  const namedUnknown = unknownEditionResolved({
    chosen: editionInfo.chosen,
    listVersion: card.listVersion,
  })
    ? null
    : (editionInfo.citesUnknownByGroup.get(card.groupIndex) ?? null)
  const currentName = card.listVersion
    ? editionDisplayName(
        card.listVersion,
        editionInfo.options.find((o) => o.id === card.listVersion)?.name ??
          card.listVersion,
        translate,
      )
    : null
  const options = [
    ...editionInfo.options.filter(
      (option) => option.id === editionInfo.readAgainstId,
    ),
    ...editionInfo.options.filter(
      (option) => option.id !== editionInfo.readAgainstId,
    ),
  ]

  const pick = (id: string) =>
    editVerdict.mutate({
      declarationId: declId,
      statementId: card.id,
      verdict: null,
      listVersion: id,
    })

  // B4: no catalogue options at all — a disabled, permanently-empty picker
  // is not an honest affordance (there is nothing to pick, ever, until the
  // catalogue grows one). A quiet hint replaces it; the amber warning above
  // (when the document cites an unknown edition) stays exactly as it was.
  const hasOptions = editionInfo.options.length > 0
  const picker = hasOptions ? (
    <Select
      value={card.listVersion ?? ""}
      onValueChange={pick}
      disabled={editVerdict.isPending}
    >
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          {/* Wrapping span, not `asChild` directly on `SelectPrimitive
              .Trigger`: see `GeltungSelect`'s own doc — the Slot clone breaks
              the trigger's click-to-open. */}
          <TooltipTrigger asChild>
            {/* `inline-flex`, not a bare span — see `GeltungSelect`'s own
                doc: a bare span blockifies as a flex item of the surrounding
                `.sv2-refquiet`/`.sv2-refwarn` row and can add line-box
                leading the `.sv2-gsel` pill never had. */}
            <span className="inline-flex">
              <SelectPrimitive.Trigger
                aria-label={translate("MC_DECL_V2_REFLIST_SELECT_ARIA")}
                className="sv2-gsel"
              >
                <span className="sv2-gsel-lbl">
                  {currentName ?? translate("MC_DECL_V2_REFLIST_PICK")}
                </span>
                <span className="sv2-car">▾</span>
              </SelectPrimitive.Trigger>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {translate("MC_DECL_V2_REFLIST_SELECT_ARIA")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <SelectContent className="max-w-[min(90vw,36rem)]">
        {options.map((o) => (
          <SelectItem
            key={o.id}
            value={o.id}
            className="sv2-selitem whitespace-normal wrap-break-word"
          >
            <span className="flex flex-col gap-0.5">
              <span>
                {editionDisplayName(o.id, o.name, translate)}
                {o.id === editionInfo.readAgainstId && (
                  <span className="text-attention">
                    {" · "}
                    {translate("MC_DECL_V2_REFLIST_RECOMMENDED")}
                  </span>
                )}
              </span>
              {formatDate(o.published) && (
                <span className="text-[10px] text-muted-foreground">
                  {formatDate(o.published)}
                </span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  ) : (
    <span
      data-testid="reflist-no-catalog"
      className="text-[11.5px] italic text-grey5"
    >
      {translate("MC_DECL_V2_REFLIST_NO_CATALOG")}
    </span>
  )

  return (
    <div className="sv2-uslot sv2-dark sv2-refslot" data-testid="edition-row">
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="sv2-ski">
              {translate("MC_DECL_V2_REFLIST_LABEL")}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {translate("MC_DECL_V2_REFLIST_LABEL_TITLE")}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {namedUnknown ? (
        <div className="sv2-refwarn" role="status">
          <TriangleAlert className="sv2-ic" />
          <span>
            {translate("MC_DECL_V2_REFLIST_UNKNOWN", { named: namedUnknown })}
          </span>
          {picker}
        </div>
      ) : (
        <div className="sv2-refquiet">
          {/* With a selected version the picker's own trigger already names
              it — repeating it as a text line rendered the same string
              twice, stacked (product-owner screenshot, 26.08.). The text
              line only carries the states the trigger cannot: read-against
              fallback and unset. */}
          {!currentName && (
            <span className="sv2-qv">
              {editionInfo.readAgainst
                ? translate("MC_DECL_V2_REFLIST_READ_AGAINST", {
                    edition: editionDisplayName(
                      editionInfo.readAgainstId ?? "",
                      editionInfo.readAgainst,
                      translate,
                    ),
                  })
                : translate("MC_DECL_V2_REFLIST_UNSET")}
            </span>
          )}
          {picker}
        </div>
      )}
    </div>
  )
}
